import { Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { BaryModalEvent, IBaryModalEvent } from '../../../../models/bary-modal-event';
import { forkJoin } from 'rxjs';
import { mapAsBody } from '../../../shared/util/request-util';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { OfferHandlingTypeExtendedService } from '../../../../services/offer-handling-type-extended.service';
import { RefDimensionExtendedService } from '../../../../services/ref-dimension-extended.service';
import { RefDimension } from '../../../../models/ref-dimension.model';
import { HandlingTypeViewModel } from '../../../../models/handling-type-view.model';
import { MDBBootstrapModulesPro, MDBModalRef } from 'ng-uikit-pro-standard';
import { TransportFormGroup } from '../../../../models/forms/TransportFormGroup';
import { HandlingTypeEnum } from '../../../../models/enums/handling-type.enum';
import { CurrentDataService } from '../../../../services/current-data-service';
import { LIST_MEASURE_UNIT, MeasureUnitEnum } from '../../../../models/vehicle.model';
import { BaryComponentHelper } from '../../../shared/util/bary-component-helper';
import { TransportItem } from '../../../../models/transport-item.model';
import { take } from 'rxjs/operators';
import { PricingTransporterConfigurationService } from '../../../../services/pricing-transporter-configuration.service';
import { IPricingTransporterConfiguration } from '../../../../models/pricing-transporter.configuration';
import { BaryValidator } from '../../../../validators/bary.validator';
import { PalletMinLDM } from '../../../../validators/pallet-min-ldm.validator';
import { HandlingType, LIST_HANDLING_TYPE } from '../../../../models/handling-type.model';
import { ObjLabelValue } from '../../../../models/obj-label-value.interface';
import { TRANSPORT_ITEM_ESTIMATE_STEP } from '../../../../models/enums/transport-item-estimate-step.enum';
import { BaryOfferFormComponent } from '../../../shared/ui/forms/bary-offer-form/bary-offer-form.component';
import { TransportEstimationStepComponent } from './settings-step/transport-estimation-step.component';
import { BaryButtonComponent } from '../../../shared/components';
import { DecimalPipe, NgIf } from '@angular/common';
import { TransportExtraConditionFormComponent } from '../transport-extra-condition-form/transport-extra-condition-form.component';
import { OfferServiceTypeEnum } from '../../../../models/enums/offer-service-type.enum';
import { FaDirectiveEnum } from '../../../../models/enums/fa-directive.enum';
import { BaryIconComponent } from '../../../shared/components/bary-icon/bary-icon.component';
import { OfferMerchandiseTypeEnum } from '../../../../models/enums/offer-merchandise-type.enum';

@Component({
  selector: 'jhi-modal-estimate',
  templateUrl: './modal-estimate.component.html',
  styleUrls: ['./modal-estimate.component.scss'],
  standalone: true,
  imports: [
    MDBBootstrapModulesPro,
    BaryOfferFormComponent,
    TransportEstimationStepComponent,
    BaryButtonComponent,
    TranslateModule,
    DecimalPipe,
    TransportExtraConditionFormComponent,
    NgIf,
    BaryIconComponent,
  ],
})
export class ModalEstimateComponent implements OnInit {
  readonly #modalRef = inject(MDBModalRef);
  readonly #translateService = inject(TranslateService);
  readonly #currentDataService = inject(CurrentDataService);
  readonly #offerHandlingTypeService = inject(OfferHandlingTypeExtendedService);
  readonly #refDimensionService = inject(RefDimensionExtendedService);
  readonly #pricingTransporterConfigurationService = inject(PricingTransporterConfigurationService);

  public action = BaryComponentHelper.generateAction<IBaryModalEvent>();

  public handlingType = HandlingTypeEnum;
  public refDimensions: RefDimension[] = [];
  public dimensionOpts: { label: string; value: number }[] = [];

  public transportFormGroup!: TransportFormGroup;
  public pricingTransporterConfiguration?: IPricingTransporterConfiguration;

  public currentStep: TRANSPORT_ITEM_ESTIMATE_STEP = TRANSPORT_ITEM_ESTIMATE_STEP.SETTING;

  protected readonly FAS = FaDirectiveEnum.FAS;
  protected readonly TRANSPORT_STEP = TRANSPORT_ITEM_ESTIMATE_STEP;
  protected readonly CONDITIONED = OfferMerchandiseTypeEnum.CONDITIONED;

  protected readonly FormGroup = FormGroup;

  public handlingTypes: HandlingTypeViewModel[] = [];
  public handlingTypesOpt: ObjLabelValue[] = [];

  public transportItemForm!: FormGroup;
  public currentTransportItem!: TransportItem;

  public CRATE_OF_WINE: HandlingTypeEnum = HandlingTypeEnum.CRATE_OF_WINE;

  public prepareForm: boolean = true;

  public initForm(transportItem: TransportItem, merchandiseType: OfferMerchandiseTypeEnum = OfferMerchandiseTypeEnum.CONDITIONED): void {
    const handlingType = new FormControl(
      { value: OfferMerchandiseTypeEnum.CONDITIONED === merchandiseType ? transportItem?.handlingType || HandlingTypeEnum.PALLET : undefined, disabled: false },
      OfferMerchandiseTypeEnum.CONDITIONED === merchandiseType ? [Validators.required] : []
    );
    const weightQuotient = (transportItem?.weightUnit?.value || MeasureUnitEnum.KG) === MeasureUnitEnum.TONNE ? 1000 : 1;
    const weight = transportItem?.weight ? transportItem.weight / weightQuotient : undefined;
    const lengthQuotient = (transportItem?.lengthUnit?.value || MeasureUnitEnum.CENTIMETER) === MeasureUnitEnum.METER ? 100 : 1;
    const width = transportItem?.width ? transportItem.width / lengthQuotient : undefined;
    const length = transportItem?.length ? transportItem.length / lengthQuotient : undefined;
    const maxHeight = transportItem?.maxHeight ? transportItem.maxHeight / lengthQuotient : undefined;

    this.transportItemForm = new FormGroup(
      {
        handlingType,
        dimension: new FormControl(
          { value: transportItem?.dimension, disabled: false },
          OfferMerchandiseTypeEnum.CONDITIONED === merchandiseType && this.hasDimension(transportItem.handlingType) ? [Validators.required] : []
        ),
        width: new FormControl({ value: width, disabled: this.isCrateOfWine(transportItem.handlingType) }, this.getValidators('width', handlingType)),
        length: new FormControl({ value: length, disabled: this.isCrateOfWine(transportItem.handlingType) }, this.getValidators('length', handlingType)),
        maxHeight: new FormControl({ value: maxHeight, disabled: this.isCrateOfWine(transportItem.handlingType) }, this.getValidators('maxHeight', handlingType)),
        weight: new FormControl({ value: weight, disabled: this.isCrateOfWine(transportItem.handlingType) }, this.getValidators('weight', handlingType)),
        volume: new FormControl({ value: transportItem?.volume, disabled: merchandiseType !== OfferMerchandiseTypeEnum.UNCONDITIONED }),
        quantity: new FormControl({ value: transportItem?.quantity, disabled: false }, [
          Validators.min(1),
          BaryValidator.maxForPalletAndParcel(
            handlingType,
            this.pricingTransporterConfiguration?.quotationItemsPalletMaxQuantity,
            this.pricingTransporterConfiguration?.quotationItemsParcelMaxQuantity
          ),
        ]),
        groundPalletQuantity: new FormControl({ value: transportItem?.groundPalletQuantity, disabled: false }),
        otherSpecificQuantity: new FormControl({ value: transportItem?.otherSpecificQuantity, disabled: false }),
        description: new FormControl({ value: transportItem?.description, disabled: false }),
        lengthUnit: new FormControl({ value: transportItem?.lengthUnit?.value || MeasureUnitEnum.CENTIMETER, disabled: OfferMerchandiseTypeEnum.CONDITIONED === merchandiseType }),
        weightUnit: new FormControl({ value: transportItem?.weightUnit?.value || MeasureUnitEnum.KG, disabled: OfferMerchandiseTypeEnum.CONDITIONED === merchandiseType }),
        volumeUnit: new FormControl({ value: transportItem?.volumeUnit?.value || MeasureUnitEnum.CUBIC_METER, disabled: true }),
        other: new FormControl({ value: transportItem?.other, disabled: false }),
        vehicleCategory: new FormControl({ value: transportItem?.vehicleCategory?.code, disabled: !!transportItem?.vehicleNotFound }),
        vehicleType: new FormControl({ value: transportItem?.vehicleType?.code, disabled: !!transportItem?.vehicleNotFound }),
        vehicleNotFound: new FormControl({ value: transportItem?.vehicleNotFound, disabled: !transportItem?.vehicleNotFound }),
        otherVehicleType: new FormControl({ value: transportItem?.otherVehicleType, disabled: !transportItem?.vehicleNotFound }),
        id: new FormControl({ value: transportItem?.id, disabled: true }),
        transport: new FormControl({ value: transportItem?.transport, disabled: true }),
      },
      { validators: [PalletMinLDM] }
    );

    if (OfferMerchandiseTypeEnum.CONDITIONED === merchandiseType) {
      this.transportItemForm.controls['quantity']!.addValidators([Validators.required]);
      this.transportItemForm.updateValueAndValidity();
    }
  }

  public updateDimensionOpts(): void {
    this.dimensionOpts = (this.refDimensions || [])
      .filter((dimension: RefDimension) => dimension.handlingType === this.transportItemForm.controls['handlingType']?.value && dimension.activated)
      .map((dimension: RefDimension) => {
        return {
          label:
            dimension.handlingType === HandlingTypeEnum.CRATE_OF_WINE
              ? (this.#translateService.instant('LABEL.FORMAT.COLON_AND_VALUE', { format: dimension.label }) as string)
              : `${dimension.label}`,
          value: dimension.id,
        };
      });
    if ([HandlingTypeEnum.PALLET, HandlingTypeEnum.CONTAINER].includes(this.transportItemForm.controls['handlingType']?.value)) {
      this.dimensionOpts.push({ label: this.#translateService.instant('LABEL.OTHER_FORMAT'), value: -1 });
    }
  }

  private getValidators(formControlName: string, handlingType: FormControl): ValidatorFn[] {
    switch (formControlName) {
      case 'length':
        if (this.isCrateOfWine()) return [];
        return [
          Validators.required,
          Validators.min(1),
          BaryValidator.maxForPalletAndParcel(
            handlingType,
            this.pricingTransporterConfiguration?.quotationItemsPalletMaxLength,
            this.pricingTransporterConfiguration?.quotationItemsParcelMaxLength
          ),
        ];
      case 'width':
        if (this.isCrateOfWine()) return [];
        return [
          Validators.required,
          Validators.min(1),
          BaryValidator.maxForPalletAndParcel(
            handlingType,
            this.pricingTransporterConfiguration?.quotationItemsPalletMaxWidth,
            this.pricingTransporterConfiguration?.quotationItemsParcelMaxWidth
          ),
        ];
      case 'maxHeight':
        if (this.isCrateOfWine()) return [];
        return [
          Validators.required,
          Validators.min(1),
          BaryValidator.maxForPalletAndParcel(
            handlingType,
            this.pricingTransporterConfiguration?.quotationItemsPalletMaxHeight,
            this.pricingTransporterConfiguration?.quotationItemsParcelMaxHeight
          ),
        ];
      case 'weight':
        if (this.isCrateOfWine()) return [];
        return [
          Validators.required,
          Validators.min(0.1),
          BaryValidator.maxForPalletAndParcel(
            handlingType,
            this.pricingTransporterConfiguration?.quotationItemsPalletMaxWeight,
            this.pricingTransporterConfiguration?.quotationItemsParcelMaxWeight
          ),
        ];
      default:
        return [];
    }
  }

  public hasDimension(handlingType?: HandlingTypeEnum): boolean {
    return !!handlingType && [HandlingTypeEnum.PALLET, HandlingTypeEnum.CRATE_OF_WINE, HandlingTypeEnum.CONTAINER].includes(handlingType!);
  }

  public isCrateOfWine(handlingType?: HandlingTypeEnum): boolean {
    return !!handlingType && [HandlingTypeEnum.CRATE_OF_WINE].includes(handlingType);
  }

  public ngOnInit(): void {
    this.prepareForm = true;
    forkJoin({
      transporter: this.#currentDataService.getCurrentTransporter(),
      customer: this.#currentDataService.getCustomer(),
      handlingTypes: this.#offerHandlingTypeService.getCurrent(),
      refDimensions: this.#refDimensionService.query(),
      config: this.#pricingTransporterConfigurationService.retrieveConfiguration().pipe(take(1)).pipe(mapAsBody),
    }).subscribe(data => {
      this.handlingTypes = data.handlingTypes;
      this.refDimensions = data.refDimensions;
      this.pricingTransporterConfiguration = data.config;
      this.initForm(this.currentTransportItem, this.transportFormGroup.controls.merchandiseType.value);
      this.initHandlingType(this.transportFormGroup.controls.merchandiseType.value);
      this.updateFormByHandlingType(this.transportItemForm.controls['handlingType'].value);
      this.updateDimensionOpts();
      if (this.dimensionOpts.length > 0 && this.transportItemForm.controls['width'].value && this.transportItemForm.controls['length'].value) {
        const dimension = this.refDimensions.find(
          dim =>
            dim.width == this.transportItemForm.controls['width'].value &&
            dim.length == this.transportItemForm.controls['length'].value &&
            dim.handlingType == this.transportItemForm.controls['handlingType'].value
        );
        if (dimension) {
          this.transportItemForm.controls['dimension'].setValue(dimension.id);
        }
      }
      this.initListeners();
      this.prepareForm = false;
    });
  }

  public initListeners(): void {
    // Si on modifie le handlingType, on met à jour la liste des dimensions et le formulaire
    this.transportItemForm.controls['handlingType']?.valueChanges.subscribe((val): void => {
      this.updateFormByHandlingType(val);
      this.updateDimensionOpts();
    });
    // Si on modifie les dimensions, on met à jour les champs suivants : length, width, maxHeight, other, otherSpecific
    this.transportItemForm.controls['dimension'].valueChanges.subscribe((dimensionId): void => {
      if (!dimensionId && dimensionId !== -1) {
        return;
      } // -1 est l'id de la valeur "Autre format pour les palettes"
      const val: RefDimension = this.refDimensions.find(dim => dim.id === dimensionId)!;
      if (this.transportItemForm.controls['handlingType']?.value !== HandlingTypeEnum.CRATE_OF_WINE && val) {
        this.transportItemForm.controls['length']?.setValue(val.length ? Math.ceil(val.length) : val.length);
        this.transportItemForm.controls['width']?.setValue(val.width ? Math.ceil(val.width) : val.width);
        this.transportItemForm.controls['maxHeight']?.setValue(val.maxHeight ? Math.ceil(val.maxHeight) : val.maxHeight);
        this.transportItemForm.controls['other']?.setValue(val.other ? Math.ceil(val.other) : val.other);
        this.transportItemForm.controls['otherSpecificQuantity'].setValue(0);
      } else {
        this.transportItemForm.controls['otherSpecificQuantity'].setValue((val?.other || 0) * this.transportItemForm.controls['quantity'].value || 0);
      }
    });
    // Si l'utilisateur modifie la valeur de la largeur, on passe alors en autre format
    this.transportItemForm.controls['width'].valueChanges.subscribe(newVal => {
      if (
        [HandlingTypeEnum.PALLET, HandlingTypeEnum.CONTAINER].includes(this.transportItemForm.controls['handlingType']?.value) &&
        !this.refDimensions.find(it => it.width === newVal)
      ) {
        this.transportItemForm.controls['dimension'].setValue(-1, { emitEvent: false });
      }
    });
    // Si l'utilisateur modifie la valeur de la longueur, on passe alors en autre format
    this.transportItemForm.controls['length'].valueChanges.subscribe(newVal => {
      if (
        [HandlingTypeEnum.PALLET, HandlingTypeEnum.CONTAINER].includes(this.transportItemForm.controls['handlingType']?.value) &&
        !this.refDimensions.find(it => it.width === newVal)
      ) {
        this.transportItemForm.controls['dimension'].setValue(-1, { emitEvent: false });
      }
    });
    // Si on modifie la quantité, on met à jour les quantités spécifiques
    this.transportItemForm.controls['quantity'].valueChanges.subscribe((val: number): void => {
      if (this.transportItemForm.controls['handlingType']?.value === HandlingTypeEnum.CRATE_OF_WINE) {
        const dimension: RefDimension = this.refDimensions.find(dim => dim.id === this.transportItemForm.controls['dimension']?.value)!;
        this.transportItemForm.controls['otherSpecificQuantity'].setValue(val * (dimension?.other || 0));
      }
    });
    // Si on modifie le type de service en FTL, la catégorie et le type de véhicule est obligatoire, sinon on utilise les données
    this.transportFormGroup.controls['serviceType'].valueChanges.subscribe(val => {
      if (val === OfferServiceTypeEnum.LTL) {
        this.transportItemForm.controls['vehicleCategory'].setValue(undefined);
        this.transportItemForm.controls['vehicleType'].setValue(undefined);
      }
    });
    // Si on modifie le type de marchandise en 'non conditionné', il n'est plus obligatoire d'avoir un handling type et une quantité
    // Mais une catégorie de véhicule et un type de véhicule sont obligatoires
    this.transportFormGroup.controls['merchandiseType'].valueChanges.subscribe(newVal => {
      if (newVal === OfferMerchandiseTypeEnum.CONDITIONED) {
        this.transportItemForm.controls['handlingType'].addValidators(Validators.required);
        this.transportItemForm.controls['quantity'].addValidators(Validators.required);
        this.transportItemForm.controls['vehicleCategory'].removeValidators(Validators.required);
        this.transportItemForm.controls['vehicleType'].removeValidators(Validators.required);
        this.transportItemForm.controls['lengthUnit'].setValue(MeasureUnitEnum.CENTIMETER);
        this.transportItemForm.controls['lengthUnit'].disable();
        this.transportItemForm.controls['weightUnit'].setValue(MeasureUnitEnum.KG);
        this.transportItemForm.controls['weightUnit'].disable();
        this.updateControl(this.transportItemForm.controls['vehicleNotFound'] as FormControl, true);
        this.updateControl(this.transportItemForm.controls['otherVehicleType'] as FormControl, true);
        this.updateControl(this.transportItemForm.controls['dimension'] as FormControl, false);
      } else {
        this.transportItemForm.controls['handlingType'].removeValidators(Validators.required);
        this.transportItemForm.controls['handlingType'].setValue(undefined);
        this.transportItemForm.controls['quantity'].removeValidators(Validators.required);
        this.transportItemForm.controls['vehicleCategory'].addValidators(Validators.required);
        this.transportItemForm.controls['vehicleType'].addValidators(Validators.required);
        this.transportItemForm.controls['lengthUnit'].setValue(MeasureUnitEnum.CENTIMETER);
        this.transportItemForm.controls['lengthUnit'].enable();
        this.transportItemForm.controls['weightUnit'].setValue(MeasureUnitEnum.KG);
        this.transportItemForm.controls['weightUnit'].enable();
        this.updateControl(this.transportItemForm.controls['vehicleNotFound'] as FormControl, false, []);
        this.updateControl(this.transportItemForm.controls['otherVehicleType'] as FormControl, false, []);
        this.updateControl(this.transportItemForm.controls['dimension'] as FormControl, true);
      }
      this.transportItemForm.updateValueAndValidity();
    });
    // Si on clique sur la checkbox pour indiquer qu'on ne trouve pas le véhicule souhaité, on désactive les champs initiaux vehicleCategory et vehicleType
    // Et on fait apparaître le champ vehicleType en désactivant son validateur required
    this.transportItemForm.controls['vehicleNotFound']?.valueChanges.subscribe((newVal: boolean) => {
      if (newVal) {
        this.updateControl(this.transportItemForm.controls['vehicleCategory'] as FormControl, true);
        this.updateControl(this.transportItemForm.controls['vehicleType'] as FormControl, true);
        this.updateControl(this.transportItemForm.controls['otherVehicleType'] as FormControl, false, []);
      } else {
        if (this.transportFormGroup.controls['merchandiseType'].value === OfferMerchandiseTypeEnum.UNCONDITIONED) {
          this.updateControl(this.transportItemForm.controls['vehicleCategory'] as FormControl, false);
          this.updateControl(this.transportItemForm.controls['vehicleType'] as FormControl, false);
          this.updateControl(this.transportItemForm.controls['otherVehicleType'] as FormControl, true);
        }
      }
      this.transportItemForm.updateValueAndValidity();
    });
  }

  /**
   * Permet de mettre à jour le formulaire en fonction "HandlingType" sélectionné.
   *
   * @param handlingTypeSelected
   * @private
   */
  private updateFormByHandlingType(handlingTypeSelected: HandlingTypeEnum): void {
    switch (handlingTypeSelected) {
      case HandlingTypeEnum.BUNDLE:
      case HandlingTypeEnum.INDIVISIBLE:
      case HandlingTypeEnum.ROLL:
      case HandlingTypeEnum.PARCEL:
      case HandlingTypeEnum.PERSONALIZED:
        this.updateControl(this.transportItemForm.controls['length'] as FormControl, false);
        this.updateControl(this.transportItemForm.controls['maxHeight'] as FormControl, false);
        this.updateControl(this.transportItemForm.controls['width'] as FormControl, false);
        this.updateControl(this.transportItemForm.controls['weight'] as FormControl, false);
        this.updateControl(this.transportItemForm.controls['dimension'] as FormControl, true);
        this.updateControl(this.transportItemForm.controls['description'] as FormControl, false);
        break;
      case HandlingTypeEnum.PALLET:
        this.updateControl(this.transportItemForm.controls['length'] as FormControl, false);
        this.updateControl(this.transportItemForm.controls['maxHeight'] as FormControl, false);
        this.updateControl(this.transportItemForm.controls['width'] as FormControl, false);
        this.updateControl(this.transportItemForm.controls['weight'] as FormControl, false);
        this.updateControl(this.transportItemForm.controls['dimension'] as FormControl, false);
        this.updateControl(this.transportItemForm.controls['description'] as FormControl, false);
        this.transportItemForm.controls['dimension'].setValue(this.currentTransportItem.dimension);
        break;
      case HandlingTypeEnum.CONTAINER:
        this.updateControl(this.transportItemForm.controls['length'] as FormControl, false);
        this.updateControl(this.transportItemForm.controls['maxHeight'] as FormControl, false, []);
        this.updateControl(this.transportItemForm.controls['width'] as FormControl, false);
        this.updateControl(this.transportItemForm.controls['weight'] as FormControl, false);
        this.updateControl(this.transportItemForm.controls['dimension'] as FormControl, false);
        this.updateControl(this.transportItemForm.controls['description'] as FormControl, false);
        this.transportItemForm.controls['dimension'].setValue(this.currentTransportItem.dimension);
        break;
      case HandlingTypeEnum.CRATE_OF_WINE:
        this.updateControl(this.transportItemForm.controls['length'] as FormControl, true);
        this.updateControl(this.transportItemForm.controls['maxHeight'] as FormControl, true);
        this.updateControl(this.transportItemForm.controls['width'] as FormControl, true);
        this.updateControl(this.transportItemForm.controls['weight'] as FormControl, true);
        this.updateControl(this.transportItemForm.controls['dimension'] as FormControl, false);
        this.updateControl(this.transportItemForm.controls['description'] as FormControl, false);
        break;
      default:
    }
  }

  /**
   * Permet de mettre à jour un formControl:
   * disabled = true, on retire la valeur + les validateurs et on désactive le champs
   * disabled = false, on ajoute les validateurs et on active le champs
   * @param formControl
   * @param disabled
   * @param validator
   * @private
   */
  private updateControl(formControl: FormControl, disabled: boolean, validator: ValidatorFn[] = [Validators.required]): void {
    if (disabled) {
      formControl.disable();
      formControl.removeValidators(validator);
      formControl.setValue(undefined);
    } else {
      formControl.enable();
      formControl.addValidators(validator);
    }
    formControl.updateValueAndValidity();
  }

  public get weightTotal(): number | null {
    if (this.transportItemForm?.controls['weight']?.value && (this.transportItemForm?.controls['quantity']?.value || 1)) {
      return this.transportItemForm.controls['weight'].value * (this.transportItemForm.controls['quantity'].value || 1);
    }
    return null;
  }

  public get surfaceTotal(): number | null {
    if (this.transportItemForm.controls['length']?.value && this.transportItemForm.controls['width']?.value && (this.transportItemForm.controls['quantity']?.value || 1)) {
      const q = this.transportItemForm.controls['lengthUnit']?.value === MeasureUnitEnum.METER ? 1 : 100;
      return (
        (((this.transportItemForm.controls['length'].value! / q) * this.transportItemForm.controls['width'].value) / q) * (this.transportItemForm.controls['quantity'].value || 1)
      );
    }
    return null;
  }

  public get volumeTotal(): number | null {
    if ((this.surfaceTotal && this.transportItemForm.controls['maxHeight']?.value) || this.transportItemForm.controls['volume']?.value) {
      const q = this.transportItemForm.controls['lengthUnit']?.value === MeasureUnitEnum.METER ? 1 : 100;
      return this.transportItemForm.controls['volume']?.value || (this.surfaceTotal! * this.transportItemForm.controls['maxHeight'].value) / q;
    }
    return null;
  }

  public get ldmTotal(): number | null {
    if (this.surfaceTotal) return this.surfaceTotal / 2.4;
    return null;
  }

  public get weightUnit(): string {
    return LIST_MEASURE_UNIT[this.transportItemForm.controls['weightUnit']?.value || MeasureUnitEnum.KG].label;
  }

  /**
   * Permet d'aller à l'étape suivante.
   */
  public nextStep(): void {
    if (
      this.currentStep === TRANSPORT_ITEM_ESTIMATE_STEP.SETTING &&
      (this.transportFormGroup.controls.isControlledTemperature.value || this.transportFormGroup.controls.isHazardousMaterial.value)
    ) {
      this.currentStep = TRANSPORT_ITEM_ESTIMATE_STEP.SPECIAL;
    } else {
      this.initHandlingType(this.transportFormGroup.controls.merchandiseType.value);
      this.currentStep = TRANSPORT_ITEM_ESTIMATE_STEP.ITEM;
    }
  }

  /**
   * Permet d'aller à l'étape précedente.
   */
  public returnStep(): void {
    if (
      this.currentStep === TRANSPORT_ITEM_ESTIMATE_STEP.ITEM &&
      (this.transportFormGroup.controls.isControlledTemperature.value || this.transportFormGroup.controls.isHazardousMaterial.value)
    ) {
      this.transportFormGroup.controls.items = [];
      this.currentStep = TRANSPORT_ITEM_ESTIMATE_STEP.SPECIAL;
    } else if (
      (this.currentStep === TRANSPORT_ITEM_ESTIMATE_STEP.ITEM &&
        !this.transportFormGroup.controls.isControlledTemperature.value &&
        !this.transportFormGroup.controls.isHazardousMaterial.value) ||
      this.currentStep === TRANSPORT_ITEM_ESTIMATE_STEP.SPECIAL
    ) {
      this.currentStep = TRANSPORT_ITEM_ESTIMATE_STEP.SETTING;
    }
  }

  /**
   * Ferme la modale si from.close = true,
   * sinon et si le formulaire est valide, on ferme la modale et on envoie le formulaire afin de l'ajouter dans la transportForm.
   *
   * @param from
   */
  public handleClick(from: any): void {
    if (from.close) {
      // If closing modal and no items, we reset at least special condition
      if (this.transportFormGroup.controls.items.length === 0) {
        this.transportFormGroup.controls.isControlledTemperature.setValue(false);
        this.transportFormGroup.controls.isHazardousMaterial.setValue(false);
      }

      this.action.next(new BaryModalEvent(from));
      this.#modalRef.hide();
      return;
    }
    if (this.transportItemForm.invalid) {
      this.transportItemForm.markAllAsTouched();
      return;
    }

    this.setValueIfCrateOfWine();

    // On définit la quantité à 1 par défaut quand on clique sur confirmer
    // On ne passera ici que pour un transport non conditionné pour lequel on n'a pas indiqué de quantité
    if (!this.transportItemForm.controls['quantity'].value) {
      this.transportItemForm.controls['quantity'].setValue(1);
    }

    // On saisit le poids par unité, mais on enregistre le poids total et on le multiplie par 1000 si on l'a saisi en tonne
    const weightCoefficient = this.transportItemForm.controls['weightUnit'].value === MeasureUnitEnum.TONNE ? 1000 : 1;
    this.transportItemForm.controls['weight'].setValue(
      this.transportItemForm.controls['weight'].value * (this.transportItemForm.controls['quantity'].value || 1) * weightCoefficient
    );

    if (this.transportItemForm.controls['width'].value && this.transportItemForm.controls['length'].value && this.transportItemForm.controls['maxHeight'].value) {
      // On multiplie les mesures par 100 si on les a saisies en mètre
      const lengthCoefficient = this.transportItemForm.controls['lengthUnit'].value === MeasureUnitEnum.METER ? 100 : 1;
      this.transportItemForm.controls['width'].setValue(this.transportItemForm.controls['width'].value * lengthCoefficient);
      this.transportItemForm.controls['length'].setValue(this.transportItemForm.controls['length'].value * lengthCoefficient);
      this.transportItemForm.controls['maxHeight'].setValue(this.transportItemForm.controls['maxHeight'].value * lengthCoefficient);
    } else {
      this.transportItemForm.controls['volume'].setValue(this.transportItemForm.controls['volume'].value * 1); // To convert it as a number
    }

    this.action.next(new BaryModalEvent({ ...from, value: this.transportItemForm, submit: true }));

    this.#modalRef.hide();
  }

  /**
   * Si le handlingType == CRATE_OF_WINE,
   * on remet les valeurs de la dimension sélectionnée dans les champs suivant, wifth, length, maxHeight, weight et other.
   *
   */
  public setValueIfCrateOfWine(): void {
    if (this.transportItemForm.controls?.['handlingType'].value === HandlingTypeEnum.CRATE_OF_WINE) {
      const val: RefDimension = this.refDimensions.find(dim => dim.id === this.transportItemForm.controls['dimension']?.value)!;
      this.transportItemForm.controls['length']?.setValue(val.length ? Math.ceil(val.length) : val.length);
      this.transportItemForm.controls['width']?.setValue(val.width ? Math.ceil(val.width) : val.width);
      this.transportItemForm.controls['maxHeight']?.setValue(val.maxHeight ? Math.ceil(val.maxHeight) : val.maxHeight);
      this.transportItemForm.controls['weight']?.setValue(val.weight ? Math.ceil(val.weight) : val.weight);
      this.transportItemForm.controls['other']?.setValue(val.other ? Math.ceil(val.other) : val.other);
    }
  }

  private initHandlingType(merchandiseType: OfferMerchandiseTypeEnum = OfferMerchandiseTypeEnum.CONDITIONED): void {
    const handlingTypes: HandlingTypeViewModel[] = OfferMerchandiseTypeEnum.CONDITIONED === merchandiseType ? this.handlingTypes || [] : [];
    this.handlingTypesOpt = handlingTypes
      .filter(handlingType => {
        if (this.transportFormGroup.controls.isControlledTemperature.value) {
          return handlingType.controlledTemperature && !handlingType.hazardousMaterial;
        }
        if (this.transportFormGroup.controls.isHazardousMaterial.value) {
          return handlingType.hazardousMaterial && !handlingType.controlledTemperature;
        }
        return !handlingType.controlledTemperature && !handlingType.hazardousMaterial;
      })
      .flatMap(v => v.handlingTypes)
      .map(v => LIST_HANDLING_TYPE[v])
      .map((handlingType: HandlingType) => ({
        label: this.#translateService.instant(handlingType.labelKey),
        value: handlingType.code,
      }));
    /** Par défault le handlingType est initialisé à "COLIS", si le transporter ne fait pas de "COLIS", on change la valeur avec le 1er handlingType du transporter **/
    if (this.handlingTypesOpt.length && !this.handlingTypesOpt.map(handlingType => handlingType.value).includes(this.transportItemForm.controls['handlingType']?.value)) {
      this.transportItemForm.controls['handlingType'].setValue(this.handlingTypesOpt[0].value);
    }
  }
}
